/**
 * 강습 관련 유틸리티 함수들
 */

/**
 * 강습 종류에서 회권/재수강 정보 제거하여 대표 강습명만 반환
 * @param {string} courseName - 원본 강습명
 * @returns {string} - 정제된 강습명
 */
export const getDisplayCourseName = (courseName) => {
    if (!courseName) return courseName;

    let displayName = courseName;

    // "재수강" 제거
    displayName = displayName.replace(/\s*재수강\s*/g, "");

    // 괄호 안의 회권 정보 제거 (예: "(10회권)", "(2회권)", "(5회)")
    displayName = displayName.replace(/\s*\([^)]*\d+\s*회권?[^)]*\)/g, "");

    // 숫자와 회/회권 패턴 제거 (예: "2회권", "3회", "10회권")
    displayName = displayName.replace(/\s*\d+\s*회권?\s*/g, "");

    // "권"으로 끝나는 단어에서 "권" 제거 (예: "특강권" → "특강")
    displayName = displayName.replace(/(\S+)권\s*$/g, "$1");

    // 앞뒤 공백 제거
    return displayName.trim();
};

/**
 * 회권 숫자 추출 함수
 * @param {string} courseName - 강습명
 * @returns {number} - 회권 숫자
 */
const extractTicketNumber = (courseName) => {
    // 1. 괄호 안의 회권 숫자 우선 (예: "(5회권)", "(2회권)")
    let match = courseName.match(/\((\d+)\s*회권?\)/);
    if (match) return parseInt(match[1]);

    // 2. 괄호 없이 회권 숫자 (예: "5회권", "2회권")
    match = courseName.match(/(\d+)\s*회권/);
    if (match) return parseInt(match[1]);

    // 3. 회 숫자 (예: "5회", "2회")
    match = courseName.match(/(\d+)\s*회/);
    if (match) return parseInt(match[1]);

    // 4. 마지막으로 첫 번째 숫자
    match = courseName.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
};

/**
 * 같은 displayName을 가진 강습들 중에서 대표 강습 선택
 * @param {string[]} courses - 강습명 배열
 * @returns {string|null} - 대표 강습명
 */
export const selectRepresentativeCourse = (courses) => {
    if (courses.length === 0) return null;
    if (courses.length === 1) return courses[0];

    let filteredCourses = courses;

    // 1. "재수강"이 없는 것 우선
    const withoutRetake = courses.filter((c) => !c.includes("재수강"));
    if (withoutRetake.length > 0) {
        filteredCourses = withoutRetake;
    }

    // 2. 숫자가 포함된 것 중에서 가장 낮은 값(최소값) 선택
    const withNumbers = filteredCourses.filter((c) => /\d+/.test(c));
    if (withNumbers.length > 0) {
        const sorted = withNumbers.sort((a, b) => {
            const numA = extractTicketNumber(a);
            const numB = extractTicketNumber(b);
            return numA - numB;
        });
        return sorted[0];
    }

    // 3. 가장 짧은 것 선택
    return filteredCourses.reduce((shortest, current) =>
        current.length < shortest.length ? current : shortest
    );
};

/**
 * 현재 시간을 30분 단위로 반올림
 * @param {Date} date - 날짜 객체
 * @returns {Date} - 반올림된 날짜 객체
 */
export const roundToNearest30Minutes = (date) => {
    const time = new Date(date);
    const minutes = time.getMinutes();
    const roundedMinutes = minutes < 30 ? 0 : 30;
    time.setMinutes(roundedMinutes, 0, 0);
    return time;
};

/**
 * 초기 시간 설정 (시작 시간과 종료 시간)
 * @returns {{ startTime: Date, endTime: Date }}
 */
export const getInitialTimeSetting = () => {
    const startTime = roundToNearest30Minutes(new Date());

    const endTime = new Date(startTime);
    endTime.setHours(startTime.getHours() + 3);

    if (endTime.getHours() < 6 || endTime.getDate() !== startTime.getDate()) {
        endTime.setHours(23, 30, 0, 0);
    }

    return { startTime, endTime };
};

/**
 * 오전 6시부터 자정까지 30분 단위 시간 배열 생성
 * @returns {Date[]} - 시간 슬롯 배열
 */
export const generateTimeSlots = () => {
    const times = [];
    for (let hour = 6; hour <= 23; hour++) {
        times.push(new Date(new Date().setHours(hour, 0, 0, 0)));
        times.push(new Date(new Date().setHours(hour, 30, 0, 0)));
    }
    return times;
};

/**
 * 시간 문자열(HH:mm:ss 또는 HH:mm)을 Date 객체로 변환
 * @param {string|Date} timeString - 시간 문자열 또는 Date 객체
 * @param {Date} baseDate - 기준 날짜
 * @returns {Date|null} - Date 객체
 */
export const parseTimeStringToDate = (timeString, baseDate = new Date()) => {
    if (!timeString) return null;
    if (timeString instanceof Date) return timeString;

    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date(baseDate);
    date.setHours(hours, minutes, 0, 0);
    return date;
};

/**
 * 중복 제거된 강습 목록 생성
 * @param {string[]} courses - 전체 강습 목록
 * @param {string} currentSelected - 현재 선택된 강습
 * @returns {Array<{brand: string, displayName: string}>} - 중복 제거된 강습 목록
 */
export const getUniqueCourses = (courses, currentSelected) => {
    const groupedByDisplayName = new Map();
    courses.forEach((course) => {
        const displayName = getDisplayCourseName(course);
        if (!groupedByDisplayName.has(displayName)) {
            groupedByDisplayName.set(displayName, []);
        }
        groupedByDisplayName.get(displayName).push(course);
    });

    const unique = [];
    groupedByDisplayName.forEach((courseList, displayName) => {
        const representativeCourse = selectRepresentativeCourse(courseList);

        if (representativeCourse) {
            unique.push({ brand: representativeCourse, displayName });
        } else if (courseList.length > 0) {
            unique.push({ brand: courseList[0], displayName });
        }
    });

    // 현재 선택된 값이 uniqueCourses에 없으면 추가
    if (currentSelected && courses.includes(currentSelected)) {
        const existsInUnique = unique.some(
            (item) => item.brand === currentSelected
        );
        if (!existsInUnique) {
            const displayName = getDisplayCourseName(currentSelected);
            const existingIndex = unique.findIndex(
                (item) => item.displayName === displayName
            );
            if (existingIndex >= 0) {
                unique[existingIndex] = {
                    brand: currentSelected,
                    displayName,
                };
            } else {
                unique.push({ brand: currentSelected, displayName });
            }
        }
    }

    // 한글 가나다 순으로 정렬
    unique.sort((a, b) => {
        const nameA = a.displayName;
        const nameB = b.displayName;
        const isKoreanA = /[가-힣]/.test(nameA.charAt(0));
        const isKoreanB = /[가-힣]/.test(nameB.charAt(0));

        if (isKoreanA && isKoreanB) return nameA.localeCompare(nameB, "ko-KR");
        if (isKoreanA && !isKoreanB) return -1;
        if (!isKoreanA && isKoreanB) return 1;
        return nameA.localeCompare(nameB);
    });

    return unique;
};
